<script setup>
import { computed } from 'vue'
import Pts2ChecklistFilter from './Pts2ChecklistFilter.vue'
import Pts2HistoryFilters from './Pts2HistoryFilters.vue'
import { useI18n } from '../../i18n.js'
import { facetItemsWithSelection } from '../../pts2/filterOptions.js'

const model = defineModel({ type: Object, required: true })
const props = defineProps({ options: { type: Object, default: () => ({}) }, loading: Boolean,
  rangeError: { type: String, default: '' } })
const emit = defineEmits(['apply', 'clear', 'close', 'options'])
const { t } = useI18n()

const tanks = computed(() => facetItemsWithSelection(props.options.TANK))
const fuels = computed(() => facetItemsWithSelection(props.options.FUEL))
const tankLabel = (option) => `№${option.number}`
const fuelLabel = (option) => option.name || t('pts2UiHistoryFuelNumber', { number: option.number })

function replaceSelection(field, selection) {
  const target = model.value[field]
  target.splice(0, target.length, ...selection)
}
</script>

<template>
  <form novalidate class="pts2-transaction-filters pts2-measurement-filters" @submit.prevent="emit('apply')" @keydown.esc.prevent="emit('close')" @keydown.enter="$event.target.tagName === 'BUTTON' || $event.preventDefault()">
    <Pts2HistoryFilters v-model="model" :loading="loading" :range-error="rangeError" :show-source="false" include-seconds inclusive-end />
    <div class="pts2-filter-selects pts2-measurement-filter-selects">
      <Pts2ChecklistFilter :model-value="model.tankNumbers" :label="t('pts2UiHistoryTankNumberLabel')" :options="tanks"
        @update:model-value="replaceSelection('tankNumbers', $event)"
        :placeholder="t('pts2UiHistoryAllTanks')" :disabled="loading || options.TANK.loading || Boolean(options.TANK.error)" :option-label="tankLabel" />
      <Pts2ChecklistFilter :model-value="model.fuelGradeIds" :label="t('pts2UiHistoryFuel')" :options="fuels"
        @update:model-value="replaceSelection('fuelGradeIds', $event)"
        :placeholder="t('pts2UiHistoryAllFuels')" :disabled="loading || options.FUEL.loading || Boolean(options.FUEL.error)" appearance="entity" :option-label="fuelLabel" />
      <label><span>{{ t('pts2UiHistoryStatus') }}</span>
        <select v-model="model.statusMode" :disabled="loading" :aria-label="t('pts2UiHistoryMeasurementState')">
          <option value="ALL">{{ t('pts2UiHistoryAllStates') }}</option><option value="OK">{{ t('pts2UiHistoryNormal') }}</option><option value="ERROR">{{ t('pts2UiHistoryError') }}</option>
        </select>
      </label>
      <label><span>{{ t('pts2UiHistoryAlarms') }}</span>
        <select v-model="model.alarmMode" :disabled="loading" :aria-label="t('pts2UiHistoryMeasurementAlarms')">
          <option value="ALL">{{ t('pts2UiHistoryAll') }}</option><option value="PRESENT">{{ t('pts2UiHistoryAlarmsPresent') }}</option><option value="ABSENT">{{ t('pts2UiHistoryNone') }}</option>
        </select>
      </label>
    </div>
    <div v-for="facet in ['TANK', 'FUEL']" :key="facet" class="pts2-facet-feedback">
      <small v-if="options[facet].loading" role="status">{{ t('pts2FilterOptionsLoading') }}</small>
      <template v-if="options[facet].error"><small class="field-error" role="alert">{{ t(options[facet].error) }}</small><button class="pc-secondary" type="button" @click="emit('options', facet, options[facet].requestedCursor, true)">{{ t('pts2FilterRetry') }}</button></template>
    </div>
    <footer>
      <button class="pc-secondary" type="button" :disabled="loading" @click="emit('clear')">{{ t('pts2UiHistoryClear') }}</button>
      <button class="pc-primary" type="submit" :disabled="loading">{{ loading ? t('pts2UiHistoryApplying') : t('pts2UiHistoryApply') }}</button>
    </footer>
  </form>
</template>
